import { useMemo } from 'react';

export interface ZoneCapacitySegment {
  id: string;
  label: string;
  value: number;
}

export interface ZoneRowDataItem {
  asin?: string;
  remainingFacings?: number;
  zoneId?: string;
  zoneName?: string;
  capacityPerFacing?: number;
}

export const useAllZoneCapacityByZones = (
  allZonesRowData: ZoneRowDataItem[],
  currentZoneRowData: ZoneRowDataItem[],
) => {
  return useMemo(() => {
    const currentZoneId = currentZoneRowData[0]?.zoneId;

    // Use allZonesRowData for other zones, currentZoneRowData for current zone
    const otherZonesData = allZonesRowData.filter((item) => item.zoneId !== currentZoneId);

    // Create zone-based capacity lookup (not ASIN-based) to handle synthetic vs real ASINs
    const zoneCapacityLookup = new Map<string, number>();
    const zoneNameLookup = new Map<string, string>();

    allZonesRowData.forEach((row) => {
      if (row.zoneId) {
        // Use the first capacity value found for this zone
        if (!zoneCapacityLookup.has(row.zoneId)) {
          zoneCapacityLookup.set(row.zoneId, row.capacityPerFacing || 0);
        }
        if (row.zoneName) {
          zoneNameLookup.set(row.zoneId, row.zoneName);
        }
      }
    });

    // Enhance current zone data with capacity by zoneId (not by ASIN)
    const enhancedCurrentZoneData = currentZoneRowData.map((currentRow) => ({
      ...currentRow,
      capacityPerFacing: zoneCapacityLookup.get(currentRow.zoneId || '') || 0,
      zoneName: zoneNameLookup.get(currentRow.zoneId || '') || currentRow.zoneName,
    }));

    const mergedData = [...otherZonesData, ...enhancedCurrentZoneData];

    // Group by zone instead of UPHL2
    const zoneGroups: Record<string, ZoneCapacitySegment> = {};

    mergedData.forEach((row) => {
      const zoneId = row.zoneId || 'unknown';
      const zoneName = row.zoneName || 'Unknown Zone';
      const capacityPerFacing = row.capacityPerFacing || 0;
      const asinCapacity = capacityPerFacing * (row.remainingFacings || 0);

      if (asinCapacity > 0) {
        if (!zoneGroups[zoneId]) {
          zoneGroups[zoneId] = {
            id: zoneId,
            label: zoneName,
            value: 0,
          };
        }

        zoneGroups[zoneId].value += asinCapacity;
      }
    });

    return Object.values(zoneGroups);
  }, [allZonesRowData, currentZoneRowData]);
};
